import { BlogPost, BlogSearchParams } from '../types/BlogTypes';

// Cache for blog data to avoid repeated fetches
let blogCache: BlogPost[] | null = null;

/**
 * Fetches all blog posts from the JSON file
 */
export const fetchBlogPosts = async (): Promise<BlogPost[]> => {
  // Return cached data if available
  if (blogCache) {
    return blogCache;
  }

  try {
    const response = await fetch('/blogData.json');
    if (!response.ok) {
      throw new Error('Failed to fetch blog data');
    }
    
    const data = await response.json();
    
    // Sort posts by date (newest first)
    const sortedPosts = data.sort((a: BlogPost, b: BlogPost) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    // Cache the sorted data
    blogCache = sortedPosts;
    
    return sortedPosts;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    return [];
  }
};

/**
 * Gets a single blog post by ID
 */
export const getBlogPostById = async (id: string): Promise<BlogPost | null> => {
  const posts = await fetchBlogPosts();
  return posts.find(post => post.id === id) || null;
};

/**
 * Formats a date for display
 */
export const formatBlogDate = (dateString: string): string => {
  // Create a date object but prevent timezone issues by directly using
  // the provided date components from the dateString
  const [year, month, day] = dateString.split('-').map(Number);
  
  // Create a date formatter using specified options
  const formatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  // Format the date using the components directly (without timezone adjustments)
  // Months are 0-indexed in JavaScript Date
  return formatter.format(new Date(year, month - 1, day));
};

/**
 * Searches blog posts based on query parameters
 */
export const searchBlogPosts = async (params: BlogSearchParams): Promise<BlogPost[]> => {
  const posts = await fetchBlogPosts();
  
  return posts.filter(post => {
    // Filter by search query
    if (params.query) {
      const query = params.query.toLowerCase();
      const inTitle = post.title.toLowerCase().includes(query);
      const inContent = post.content.toLowerCase().includes(query);
      const inSummary = post.summary.toLowerCase().includes(query);
      const inAuthor = post.author.toLowerCase().includes(query);
      
      if (!(inTitle || inContent || inSummary || inAuthor)) {
        return false;
      }
    }
    
    // Filter by tag
    if (params.tag && !post.tags.includes(params.tag)) {
      return false;
    }
    
    return true;
  });
};

/**
 * Gets all unique tags from blog posts
 */
export const getAllTags = async (): Promise<string[]> => {
  const posts = await fetchBlogPosts();
  
  // Extract all tags and remove duplicates
  const allTags = posts.flatMap(post => post.tags);
  return [...new Set(allTags)].sort();
};

/**
 * Converts markdown content to HTML
 * This is a simple implementation - consider using a library like 'marked' for production
 */
export const markdownToHtml = (markdown: string): string => {
  let html = markdown;
  
  // Preserve iframe tags (for embedded videos)
  const iframes: string[] = [];
  // Use a simpler regex approach to find iframe tags
  let iframeMatch;
  const iframeRegex = /<iframe[\s\S]*?<\/iframe>/g;
  while ((iframeMatch = iframeRegex.exec(html)) !== null) {
    const placeholder = `__IFRAME_${iframes.length}__`;
    iframes.push(iframeMatch[0]);
    html = html.substring(0, iframeMatch.index) + placeholder + html.substring(iframeMatch.index + iframeMatch[0].length);
    // Reset regex to avoid infinite loop when using global flag
    iframeRegex.lastIndex = iframeMatch.index + placeholder.length;
  }
  
  // Convert headers
  html = html.replace(/^# (.*$)/gm, '<h1>$1</h1>');
  html = html.replace(/^## (.*$)/gm, '<h2>$1</h2>');
  html = html.replace(/^### (.*$)/gm, '<h3>$1</h3>');
  
  // Convert bold text
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Convert italic text
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Convert lists
  html = html.replace(/^\- (.*$)/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n)+/g, '<ul>$&</ul>');
  
  // Convert numbered lists
  html = html.replace(/^\d+\. (.*$)/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n)+/g, '<ol>$&</ol>');
  
  // Convert paragraphs
  html = html.replace(/^(?!<[hou]|<li|\s*$)(.*$)/gm, '<p>$1</p>');
  
  // Restore iframe tags
  iframes.forEach((iframe, index) => {
    html = html.replace(`__IFRAME_${index}__`, `<div class="video-container">${iframe}</div>`);
  });
  
  return html;
}; 